/**
 * The rows of the Goals and Engagement cards: name, Control → leader, change,
 * confidence and a likely-range bar on one scale per card. Goal rows are a
 * listbox: clicking one makes it the goal (the model saves it and everything
 * below recalculates); the primary goal wears the star. Engagement rows are
 * read-only and carry a better / worse tag. Definitions live in the column
 * header tooltips (Confidence and Likely range), never in the card.
 *
 * Treatment (Paper "Experiments · Results · Goals list · polish", 2026-10-23):
 * sentence-case headers, a fixed icon slot per row, selection as a hairline
 * ring, no orange (the answer's one orange lives in the detail card, and only
 * when the goal can be called), neutral better / worse tags.
 */

import type { ReactNode } from "react"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  ArrowDown02Icon,
  ArrowTurnBackwardIcon,
  Clock01Icon,
  CreditCardIcon,
  Cursor01Icon,
  DollarCircleIcon,
  InformationCircleIcon,
  LinkSquare02Icon,
  Note01Icon,
  PlayCircleIcon,
  StarIcon,
} from "@hugeicons/core-free-icons"
import { Skeleton } from "@/components/ui/skeleton"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { CALL_CHANCE } from "@/lib/experiments/goalMetric"

export interface ResultRow {
  key: string
  label: string
  /** Muted text after the name ("lower is better"). */
  note?: string | null
  isPrimary?: boolean
  available: boolean
  reason?: string
  rates: string | null
  change: string | null
  /** Engagement only: whether the change is good news. */
  tag?: "better" | "worse" | "same" | null
  /** Tag in color only when the confidence backs it. */
  tagStrong?: boolean
  chance: string | null
  confidence: number | null
  range: { lower: number; upper: number; point: number } | null
  /** Inline control shown on hover (e.g. the click-set pencil). */
  action?: ReactNode
}

export const RESULT_TIPS = {
  rates: "The rate per visitor: Control → the leading version.",
  change: "How much higher or lower the leading version is than Control.",
  confidence:
    "How sure we are the leading version really beats Control and it isn't luck: 99% is about 99 in 100. 50% is a coin flip; a call needs 95%.",
  confidenceMulti:
    "How sure we are the leading version is the best of all versions and it isn't luck: 99% is about 99 in 100. A call needs 95%.",
  range: "Where the real change most likely sits (95% of the time). The tick is today's change; the line is no change.",
  engagementConfidence: "How sure we are the change is real and not luck, in the direction shown by the tag.",
} as const

const GRID = "grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-4 sm:grid-cols-[minmax(0,1.4fr)_180px_104px_96px_minmax(0,1fr)]"
const TH = "text-xs leading-4 text-ui-meta"

/** One glyph per kind of goal or metric, so the list scans without reading. */
const ROW_ICON: Array<[RegExp, typeof StarIcon]> = [
  [/^clicks/, Cursor01Icon],
  [/^destination/, LinkSquare02Icon],
  [/^forms/, Note01Icon],
  [/^trials/, PlayCircleIcon],
  [/^payments/, CreditCardIcon],
  [/^revenue/, DollarCircleIcon],
  [/^bounce/, ArrowTurnBackwardIcon],
  [/^scroll/, ArrowDown02Icon],
  [/^(time|launch)/, Clock01Icon],
]
const rowIcon = (key: string) => ROW_ICON.find(([re]) => re.test(key))?.[1] ?? Cursor01Icon

export function Hint({ text, children }: { text: string; children: ReactNode }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent className="max-w-[280px]">{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function HeadCell({ children, tip, className }: { children: ReactNode; tip?: string; className?: string }) {
  if (!tip) return <span className={cn(TH, className)}>{children}</span>
  return (
    <Hint text={tip}>
      <span className={cn(TH, "flex w-fit cursor-help items-center gap-1", className)}>
        {children}
        <HugeiconsIcon icon={InformationCircleIcon} size={12} className="shrink-0 text-ui-icon" aria-hidden />
      </span>
    </Hint>
  )
}

/** One scale for every bar in a card: the rows' ranges, 0 included, clipped to −100%…+200%. */
export function rangeDomain(rows: Array<{ range: ResultRow["range"] }>): [number, number] {
  let lo = -20
  let hi = 20
  for (const r of rows) {
    if (!r.range) continue
    lo = Math.min(lo, r.range.lower)
    hi = Math.max(hi, r.range.upper)
  }
  return [Math.max(-100, lo), Math.min(200, hi)]
}

export function LikelyRangeBar({
  range,
  domain,
  strong,
  height = 12,
  className,
}: {
  range: { lower: number; upper: number; point: number }
  domain: [number, number]
  strong: boolean
  height?: number
  className?: string
}) {
  const [min, max] = domain
  const x = (v: number) => ((Math.min(max, Math.max(min, v)) - min) / (max - min || 1)) * 100
  const bar = Math.max(4, Math.round(height * 0.4))
  // HTML, not a stretched SVG, so the dot stays round at any width. Ink only:
  // the page's one orange is the callable answer, never a row.
  return (
    <div className={cn("relative w-full", className)} style={{ height }} aria-hidden>
      <span className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-border" />
      <span
        className={cn("absolute top-1/2 -translate-y-1/2 rounded-full", strong ? "bg-muted-foreground/45" : "bg-muted-foreground/25")}
        style={{ left: `${x(range.lower)}%`, width: `${Math.max(0.5, x(range.upper) - x(range.lower))}%`, height: bar }}
      />
      <span className="absolute inset-y-0 w-px -translate-x-1/2 bg-ring" style={{ left: `${x(0)}%` }} />
      <span
        className={cn("absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full", strong ? "bg-foreground" : "bg-muted-foreground")}
        style={{ left: `${x(range.point)}%`, width: bar + 2, height: bar + 2 }}
      />
    </div>
  )
}

// Neutral on purpose: green and red are for confirmations, not data.
const TAG = {
  better: "bg-muted text-foreground",
  worse: "bg-muted text-muted-foreground",
  same: "bg-muted text-muted-foreground",
}
const TAG_LABEL = { better: "Better", worse: "Worse", same: "Same" }

export function ResultRows({
  rows,
  nameHeader,
  ratesHeader,
  confidenceTip,
  selectedKey,
  onSelect,
  loading = false,
  footer,
  label,
}: {
  rows: ResultRow[]
  nameHeader: string
  ratesHeader: string
  confidenceTip: string
  /** Goal rows only: the selected row. Omit for read-only rows. */
  selectedKey?: string
  onSelect?: (key: string) => void
  loading?: boolean
  /** Extra rows under the list (Connect Stripe, + Add goal). */
  footer?: ReactNode
  label: string
}) {
  const domain = rangeDomain(rows)
  const selectable = Boolean(onSelect)
  return (
    <div className="px-3 pb-2">
      <div className={cn(GRID, "mb-1 border-b border-border px-2 pb-2")}>
        <HeadCell>{nameHeader}</HeadCell>
        <HeadCell className="hidden truncate whitespace-nowrap sm:block">
          {ratesHeader}
        </HeadCell>
        <HeadCell>Change</HeadCell>
        <HeadCell tip={confidenceTip}>Confidence</HeadCell>
        <HeadCell tip={RESULT_TIPS.range} className="hidden sm:flex">
          Likely range
        </HeadCell>
      </div>
      <ul role={selectable ? "listbox" : "list"} aria-label={label} className="flex flex-col">
        {rows.map((r) => {
          const selected = selectable && r.key === selectedKey
          const strong = r.confidence != null && r.confidence >= CALL_CHANCE
          return (
            <li
              key={r.key}
              role={selectable ? "option" : undefined}
              aria-selected={selectable ? selected : undefined}
              tabIndex={selectable ? 0 : undefined}
              onClick={selectable ? () => onSelect!(r.key) : undefined}
              onKeyDown={
                selectable
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault()
                        onSelect!(r.key)
                      }
                    }
                  : undefined
              }
              className={cn(
                GRID,
                "group min-h-11 rounded-lg px-2 py-2 outline-none transition-colors",
                selectable && "cursor-pointer focus-visible:ring-2 focus-visible:ring-ring/50",
                selected ? "bg-muted/50 ring-1 ring-input" : selectable && "hover:bg-muted/50",
                !selected && "border-t border-ui-rowline first:border-t-0",
              )}
            >
              <span className="flex min-w-0 items-center gap-2.5">
                <HugeiconsIcon icon={rowIcon(r.key)} size={16} className={cn("shrink-0", selected ? "text-foreground" : "text-ui-icon")} aria-hidden />
                <span className={cn("truncate text-[13px] font-medium leading-[18px]", r.available ? "text-foreground" : "text-muted-foreground", selected && "font-semibold")}>
                  {r.label}
                </span>
                {r.note && <span className="shrink-0 text-xs text-ui-meta">{r.note}</span>}
                {r.isPrimary && (
                  <span className="flex h-5 shrink-0 items-center gap-1 rounded-[5px] bg-card px-1.5 ring-1 ring-border" aria-label="Primary goal">
                    <HugeiconsIcon icon={StarIcon} size={11} className="fill-current text-foreground" aria-hidden />
                    <span className="text-[11px] font-medium leading-[14px] text-foreground">Primary</span>
                  </span>
                )}
                {r.action && <span className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">{r.action}</span>}
              </span>
              {!r.available ? (
                <span className="text-right text-xs leading-4 text-muted-foreground sm:col-span-4 sm:text-left">{r.reason}</span>
              ) : (
                <>
                  <span className="hidden text-[13px] leading-4 tabular-nums text-muted-foreground sm:block">
                    {loading ? <Skeleton className="h-[13px] w-24" /> : (r.rates ?? "—")}
                  </span>
                  <span className="flex items-center gap-1.5">
                    {loading ? (
                      <Skeleton className="h-4 w-12" />
                    ) : (
                      <span className={cn("text-[15px] font-semibold leading-5 tracking-[-0.01em] tabular-nums", r.change ? "text-foreground" : "text-muted-foreground/60")}>
                        {r.change ?? "—"}
                      </span>
                    )}
                    {r.tag && !loading && (
                      <span className={cn("rounded-[5px] px-1.5 text-[11px] font-medium leading-[18px]", r.tagStrong ? TAG[r.tag] : TAG.same)}>{TAG_LABEL[r.tag]}</span>
                    )}
                  </span>
                  <span
                    className={cn(
                      "text-[13px] leading-5 tabular-nums",
                      r.confidence == null
                        ? "text-muted-foreground/60"
                        : strong
                          ? "font-semibold text-foreground"
                          : r.confidence >= 0.8 || r.confidence <= 0.2
                            ? "font-medium text-foreground"
                            : "text-muted-foreground",
                    )}
                  >
                    {loading ? <Skeleton className="h-4 w-8" /> : (r.chance ?? "—")}
                  </span>
                  <span className="hidden pr-2 sm:block">
                    {r.range && !loading ? <LikelyRangeBar range={r.range} domain={domain} strong={strong} /> : null}
                  </span>
                </>
              )}
            </li>
          )
        })}
      </ul>
      {footer}
    </div>
  )
}
